import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';

import Modal from '../../../../components/Modal/Modal';
import Text from '../../../../components/Text/Text';
import CustomSelect from '../../../../components/Select/CustomSelect';
import Input from '../../../../components/Input/Input';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import Alert from '../../../../components/Alert/Alert';
import AlertType from '../../../../common/alertType';
import { coOrderStatusList } from '../../../../constants/coOrderStatus';
import { coOrderAssemblyStyleList, coOrderAssemblyTypeList } from '../../../../constants/coOrderAssemblyType';
import urgencyStatus, { urgencyStatusList } from '../../../../constants/urgencyStatus';
import patchCoOrder from '../../../../thunks/coilovers/orders/patchCoOrder';
import { clearPatchCoOrders } from '../../../../actions/coilovers/orders/patchCoOrders';
import getUsers from '../../../../thunks/users/getUsers';
import PermissionsGate from '../../../../components/PermissionsGate/PermissionsGate';
import { permissions } from '../../../../common/permissionGroup';

const modalBodyClassName = 'w-[500px] overflow-auto px-[10px]';
const rowClassName = 'flex flex-row justify-between gap-[16px] mb-[16px]';
const columnClassName = 'flex-1';
const dividerClassName = 'mr-[20px]';
const labelContainerClassName = 'mb-[6px]';
const labelTextClassName = 'text-[13px]';
const inputClassName = 'py-[11px] px-[13px] font-normal text-[13px]';

const CoOrderEditModal = ({ visible, toggleVisible, item }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const dispatch = useDispatch();
  const {
    patchCoOrder: {
      loading, error: patchError, data: patchedData
    },
    getUsers: {
      loading: getUsersLoading,
      users,
    }
  } = useSelector((state) => ({
    patchCoOrder: state?.patchCoOrder,
    getUsers: state?.getUsers
  }), shallowEqual);

  useEffect(() => {
    if (Object.keys(patchedData).length) {
      enqueueSnackbar('Order update successfully', { variant: 'success' });
      dispatch(clearPatchCoOrders());
      toggleVisible();
    }
  }, [patchedData]);

  useEffect(() => {
    if (!users.length) {
        dispatch(getUsers());
    }
  }, [users]);

  const {
    _id,
    order_id: orderId,
    order_po_number: poNumber,
    order_car_lift_position: liftPosition,
    order_reference_no: referenceNo,
    order_item_mpn: itemMpn,
    order_listing_car_make: carMake,
    order_listing_car_model: carModel,
    order_listing_car_year_range: carYearRange,
    order_label_file: labelFile,
    order_label_file2: labelFile2,
    order_shipment_tracking_number: trackingNumber,
    order_assembly_invoice: assemblyInvoice,
    order_notes: notes,
    order_status: status,
    order_assembly_type: assemblyType,
    order_assembly_style: assemblyStyle,
    order_assembly_urgency: assemblyUrgency,
    order_part_box_by: partBoxBy,
    order_picked_by: pickedBy,
    order_assembled_by: assembleBy,
    order_packed_by: packedBy
  } = item;

  const {
    values: {
      orderLiftPosition,
      orderPoNumber,
      orderReferenceNo,
      orderItemMpn,
      orderCarMake,
      orderCarModel,
      orderCarYearRange,
      orderLabelFile,
      orderLabelFile2,
      orderTrackingNumber,
      orderNotes,
      orderStatus,
      orderAssemblyType,
      orderAssemblyStyle,
      orderAssemblyUrgency,
      orderAssemblyInvoice,
      orderPartBoxBy,
      orderPickedBy,
      orderAssembleBy,
      orderPackedBy,
    },
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    isValid,
    errors,
  } = useFormik({
    initialValues: {
      orderLiftPosition: '',
      orderPoNumber: '',
      orderReferenceNo: '',
      orderItemMpn: '',
      orderCarMake: '',
      orderCarModel: '',
      orderCarYearRange: '',
      orderLabelFile: '',
      orderLabelFile2: '',
      orderTrackingNumber: '',
      orderNotes: '',
      orderStatus: {},
      orderAssemblyType: {},
      orderAssemblyStyle: null,
      orderAssemblyUrgency: {},
      orderPartBoxBy: {},
      orderPickedBy: {},
      orderAssembleBy: {},
      orderPackedBy: {},
      orderAssemblyInvoice: '',
    },
    onSubmit: (values) => {
      dispatch(patchCoOrder({
        _id,
        formData: {
          order_po_number: values?.orderPoNumber,
          order_car_lift_position: values?.orderLiftPosition,
          order_reference_no: values?.orderReferenceNo,
          order_item_mpn: values?.orderItemMpn?.toLowerCase(),
          order_listing_car_make: values?.orderCarMake,
          order_listing_car_model: values?.orderCarModel,
          order_listing_car_year_range: values?.orderCarYearRange,
          order_label_file: values?.orderLabelFile,
          order_label_file2: values?.orderLabelFile2,
          order_shipment_tracking_number: values?.orderTrackingNumber,
          order_notes: values?.orderNotes,
          order_status: values?.orderStatus?.value,
          order_assembly_type: values?.orderAssemblyType?.value,
          order_assembly_style: values?.orderAssemblyStyle?.value,
          order_assembly_urgency: values?.orderAssemblyUrgency?.value,
          order_assembly_invoice: values?.orderAssemblyInvoice,
          order_part_box_by: values?.orderPartBoxBy?.value,
          order_picked_by: values?.orderPickedBy?.value,
          order_assembled_by: values?.orderAssembleBy?.value,
          order_packed_by: values?.orderPackedBy?.value,
        }
      }));
    },
    validationSchema: Yup.object({
      orderPoNumber: Yup.string().required('PO Number is required'),
      orderItemMpn: Yup.string().required('Mpn is required'),
      orderTrackingNumber: Yup.string().min(3, 'Tracking must be at least 3 characters'),
      orderStatus: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
      orderAssemblyType: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
      orderAssemblyUrgency: Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      }),
    })
  });

  useEffect(() => {
    setValues({
      orderLiftPosition: liftPosition,
      orderPoNumber: poNumber,
      orderReferenceNo: referenceNo,
      orderItemMpn: itemMpn,
      orderCarModel: carModel,
      orderCarMake: carMake,
      orderCarYearRange: carYearRange,
      orderLabelFile: labelFile,
      orderLabelFile2: labelFile2,
      orderTrackingNumber: trackingNumber || '',
      orderNotes: notes,
      orderStatus: { label: status, value: status },
      orderAssemblyType: { label: assemblyType, value: assemblyType },
      orderAssemblyStyle: { label: assemblyStyle, value: assemblyStyle },
      orderAssemblyUrgency: {
        label: assemblyUrgency || urgencyStatus.NORMAL,
        value: assemblyUrgency || urgencyStatus.NORMAL
      },
      orderPartBoxBy: {
        label: partBoxBy,
        value: partBoxBy
      },
      orderPickedBy: {
        label: pickedBy,
        value: pickedBy
      },
      orderAssembleBy: {
        label: assembleBy,
        value: assembleBy
      },
      orderPackedBy: {
        label: packedBy,
        value: packedBy
      },
      orderAssemblyInvoice: assemblyInvoice || ''
    }, true);
  }, [item]);

  return (
    <>
      <Modal
        visible={visible}
        toggleVisible={() => {
          toggleVisible();
          dispatch(clearPatchCoOrders());
        }}
        title="Edit part"
        subTitle={`id: ${orderId} | po: ${poNumber}`}
        onSave={() => setConfirmVisible(true)}
        canSave={!isValid || loading}
      >
        <div className={`${modalBodyClassName} customScroll`}>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order PO:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderPoNumber"
                value={orderPoNumber || ''}
                placeholder="Order PO"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderPoNumber}
              />
            </div>
            <span className={dividerClassName} />
            <div className={columnClassName}>
              <Text text="Reference No:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderReferenceNo"
                value={orderReferenceNo || ''}
                placeholder="Reference No"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderReferenceNo}
              />
            </div>
          </div>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order type:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <CustomSelect
                value={[orderAssemblyType]}
                options={coOrderAssemblyTypeList}
                onChange={(data) => setFieldValue('orderAssemblyType', { ...data })}
              />
            </div>
            <span className={dividerClassName} />
            <div className={columnClassName}>
              <Text text="Order style:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <CustomSelect
                value={[orderAssemblyStyle]}
                options={[...coOrderAssemblyStyleList, { label: 'Empty', value: null }]}
                onChange={(data) => setFieldValue('orderAssemblyStyle', { ...data })}
              />
            </div>
          </div>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order status:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <CustomSelect
                value={[orderStatus]}
                options={coOrderStatusList}
                onChange={(data) => setFieldValue('orderStatus', { ...data })}
              />
            </div>
            <span className={dividerClassName} />
            <div className={columnClassName}>
              <Text text="Order Urgency:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <CustomSelect
                value={[orderAssemblyUrgency]}
                options={urgencyStatusList}
                onChange={(data) => setFieldValue('orderAssemblyUrgency', { ...data })}
              />
            </div>
          </div>
          <br />
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order Mpn:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderItemMpn"
                value={orderItemMpn || ''}
                placeholder="Order mpn"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderItemMpn}
              />
            </div>
            <span className={dividerClassName} />
            <div className={columnClassName}>
              <Text text="Lift position:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderLiftPosition"
                value={orderLiftPosition || ''}
                placeholder="Part qty"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderLiftPosition}
              />
            </div>
          </div>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order Make:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderCarMake"
                value={orderCarMake || ''}
                placeholder="Order make"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderCarMake}
              />
            </div>
            <span className={dividerClassName} />
            <div className={columnClassName}>
              <Text text="Order Model:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderCarModel"
                value={orderCarModel || ''}
                placeholder="Order model"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderCarModel}
              />
            </div>
            <span className={dividerClassName} />
            <div className={columnClassName}>
              <Text text="Order Years:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderCarYearRange"
                value={orderCarYearRange || ''}
                placeholder="Order Years"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderCarYearRange}
              />
            </div>
          </div>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order label file:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderLabelFile"
                value={orderLabelFile || ''}
                placeholder="Order label file"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderLabelFile}
              />
            </div>
          </div>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order label file2:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderLabelFile2"
                value={orderLabelFile2 || ''}
                placeholder="Order label file"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderLabelFile2}
              />
            </div>
          </div>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order tracking:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderTrackingNumber"
                value={orderTrackingNumber || ''}
                placeholder="Order tracking"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderTrackingNumber}
              />
            </div>
          </div>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order invoice:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderAssemblyInvoice"
                value={orderAssemblyInvoice || ''}
                placeholder="Order tracking"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderAssemblyInvoice}
              />
            </div>
          </div>
          <PermissionsGate can={permissions.ADMIN_ADMIN}>
            <div className={rowClassName}>
              <div className={columnClassName}>
                <Text text="Part box by:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
                <CustomSelect
                  value={[orderPartBoxBy]}
                  options={users?.map(({ email }) => ({
                  label: email,
                  value: email
                }))}
                  isSearchable
                  isLoading={getUsersLoading}
                  onChange={(data) => setFieldValue('orderPartBoxBy', { ...data })}
                />
              </div>
              <span className={dividerClassName} />
              <div className={columnClassName}>
                <Text text="Assembled by:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
                <CustomSelect
                  value={[orderAssembleBy]}
                  options={users?.map(({ email }) => ({
                  label: email,
                  value: email
                }))}
                  isSearchable
                  isLoading={getUsersLoading}
                  onChange={(data) => setFieldValue('orderAssembleBy', { ...data })}
                />
              </div>
            </div>
            <br />
            <div className={rowClassName}>
              <div className={columnClassName}>
                <Text text="Picked by:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
                <CustomSelect
                  value={[orderPickedBy]}
                  options={users?.map(({ email }) => ({
                  label: email,
                  value: email
                }))}
                  isSearchable
                  isLoading={getUsersLoading}
                  onChange={(data) => setFieldValue('orderPickedBy', { ...data })}
                />
              </div>
              <span className={dividerClassName} />
              <div className={columnClassName}>
                <Text text="Packed by:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
                <CustomSelect
                  value={[orderPackedBy]}
                  options={users?.map(({ email }) => ({
                  label: email,
                  value: email
                }))}
                  isSearchable
                  isLoading={getUsersLoading}
                  onChange={(data) => setFieldValue('orderPackedBy', { ...data })}
                />
              </div>
            </div>
            <br />
          </PermissionsGate>
          <div className={rowClassName}>
            <div className={columnClassName}>
              <Text text="Order note:" styleContainer={labelContainerClassName} textStyle={labelTextClassName} />
              <Input
                containerStyle="d-col"
                inputStyle={inputClassName}
                name="orderNotes"
                value={orderNotes || ''}
                placeholder="Order note"
                onBlur={handleBlur}
                onChange={handleChange}
                errorText={errors?.orderNotes}
                multiline
              />
            </div>
          </div>
          {patchError && <Alert text={patchError} type={AlertType.warning} />}
        </div>
      </Modal>
      <ConfirmModal
        onConfirm={() => {
          setConfirmVisible(false);
          handleSubmit();
        }}
        visible={confirmVisible}
        toggleVisible={() => {
          setConfirmVisible(false);
          toggleVisible();
        }}
      />
    </>
  );
};

CoOrderEditModal.propTypes = {
  toggleVisible: PropTypes.func,
  visible: PropTypes.bool,
  item: PropTypes.shape({
    _id: PropTypes.string,
    order_car_lift_position: PropTypes.string,
    order_id: PropTypes.string,
    order_po_number: PropTypes.string,
    order_reference_no: PropTypes.string,
    order_item_mpn: PropTypes.string,
    order_listing_car_make: PropTypes.string,
    order_listing_car_model: PropTypes.string,
    order_listing_car_year_range: PropTypes.string,
    order_label_file: PropTypes.string,
    order_label_file2: PropTypes.string,
    order_shipment_tracking_number: PropTypes.string,
    order_notes: PropTypes.string,
    order_status: PropTypes.string,
    order_assembly_type: PropTypes.string,
    order_assembly_style: PropTypes.string,
    order_assembly_urgency: PropTypes.string,
    order_assembly_invoice: PropTypes.string,
    order_part_box_by: PropTypes.string,
    order_picked_by: PropTypes.string,
    order_assembled_by: PropTypes.string,
    order_packed_by: PropTypes.string,
  })
};

CoOrderEditModal.defaultProps = {
  toggleVisible: () => {},
  visible: false,
  item: {}
};

export default CoOrderEditModal;
